import { memo, useState, useCallback, useMemo } from 'react';
import merge from 'lodash.merge';

import { downloadFile } from '@/utils/downloadFile';
import { getByPath } from '@/utils/widgets';
import { getFileIconStr } from '@/utils';

import LoadingIndicator from '@/components/ui/LoadingIndicator';
import { UploadFile } from './editors/uploadFile/uploadFile.editor.fileCompact';
import { TextEllipsis } from '@/components/ui/utils/textEllipsis/textEllipsis';
import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import { type WidgetData } from '@/components/ui/widget/widget.type';
import { type FileCompactView } from './fileCompact.view.type';
import { TextView } from '@/components/ui/widget/views/text/text.view.type';

type Props = {
  view: FileCompactView;
  data: WidgetData;
  update: any; // TODO: type this
};

export type FileCompactData = {
  mimetype?: string;
  name?: string;
};

// compact file view
const FileCompactImpl = memo(({ view, data, update }: Props) => {
  const [loading, setLoading] = useState(false);
  const [downloadResponse, setDownloadResponse] = useState(null);

  const { path, defaultIcon, editor } = view;
  const { patch, onChange, setIsPatched, editorOverrides = {} } = update || {};

  const dataWithPatchApplied = useMemo(
    () => merge({}, data, patch),
    [data, patch]
  );

  const fileData = getByPath<FileCompactData>(dataWithPatchApplied, path);

  const downloadFileFn = useCallback(
    () => downloadFile(fileData, setLoading, loading, setDownloadResponse),
    [fileData]
  );

  const disabledPath = editor?.disabledPath;
  const disabledFromData = !!getByPath(data, disabledPath);

  const disabled = editorOverrides.disabled || !onChange || disabledFromData;

  if ((!fileData || typeof fileData !== 'object') && !editor) return null;

  switch (editor?.type) {
    case 'uploadFile':
      return (
        <UploadFile
          path={path}
          data={data}
          fileData={fileData}
          onChange={onChange}
          setIsPatched={setIsPatched}
          disabled={disabled}
          defaultIcon={defaultIcon}
        />
      );
    default: {
      const iconStr = getFileIconStr(
        fileData?.mimetype,
        fileData?.name,
        defaultIcon || 'DocumentDownload'
      );

      return (
        <LoadingIndicator
          spinning={loading}
          showSuccess={downloadResponse !== null}
        >
          <div
            className="flex h-full grow cursor-pointer flex-row flex-nowrap items-center justify-start overflow-hidden"
            onClick={downloadFileFn}
            data-cy="download-link"
          >
            <DynamicIcon icon={iconStr} size={24} />
            <TextEllipsis
              className="ml-1 grow overflow-hidden underline"
              text={fileData?.name}
              middleEllipsis
            />
          </div>
        </LoadingIndicator>
      );
    }
  }
});

// sort defaults to text on file name
const getSortConfig = (view: FileCompactView): TextView => ({
  type: 'text',
  path: `${view.path}.name`
});

// filtering defaults to text search on file name
const getFilterConfig = (view: FileCompactView): TextView => ({
  type: 'text',
  path: `${view.path}.name`
});

// width defaults to 'medium'
const getDefaultWidth = () => 'medium' as const;

// return file name
const getStringifyFunction = (view: FileCompactView) => {
  const { path } = view;
  return (data: WidgetData) => {
    const fileData = getByPath(data, path);
    return (fileData && fileData.name) || '';
  };
};

export const fileCompactViewProvider = {
  component: FileCompactImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
